'use client';

import dynamic from 'next/dynamic';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import CardLocation from '@alltrails/amplitude/enums/CardLocation';
import TrailDetailsShareOption from '@alltrails/amplitude/enums/TrailDetailsShareOption';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsShareThisTrailOptionClicked from '@alltrails/amplitude/events/logTrailDetailsShareThisTrailOptionClicked';
import useUser from '@alltrails/context/hooks/useUser';
import { PageStrings } from '@alltrails/core/constants/pageStringHelpers';
import type TrailDetails from '@alltrails/core/types/TrailDetails';
import Button from '@alltrails/denali/components/Button';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import { getTrailShareObject, TrailPage } from '@alltrails/trails';
import useIsTrailMapOpen from '@/hooks/useIsTrailMapOpen';
import styles from './MobileFooter.module.scss';

const HitTheTrailModal = dynamic(() => import('@/components/HitTheTrailModal/HitTheTrailModal'), { ssr: false });
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });
const ShareModal = dynamic(() => import('@alltrails/sharing/components/ShareModal/ShareModal'), { ssr: false });

type Props = {
  trailDetails: TrailDetails;
  trailPage: TrailPage;
  isScrollLimitActive?: boolean;
};

const reduxListsAnalyticsInfo = { detailedCardLocation: 'mobile web trail details cta bar' as CardLocation, page: PageStrings.TRAIL_PAGE };

// https://www.figma.com/design/oevB4bXh8KLLDe1Yp8rBW2/Trail-Page-Web-%5BHandoff%5D?node-id=15304-253213&m=dev
export default function MobileFooter({ trailDetails, trailPage }: Props) {
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const { openTrailMap } = useIsTrailMapOpen();
  const user = useUser();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const { listModal, signUpModal } = useReduxListItems(undefined, false /* preventAutoInitialize */, reduxListsAnalyticsInfo);

  return (
    <div className={styles.footer}>
      <Button
        onClick={() => {
          logTrailDetailsActionTapped({
            trail_id: trailDetails.id.toString(),
            trail_page_action: 'download overflow menu button',
            trail_page_location: 'mobile cta bar'
          });
          setIsDownloadModalOpen(true);
        }}
        text={<FormattedMessage defaultMessage="Download" />}
        variant="accent"
      />
      <Button
        onClick={() => {
          logTrailDetailsActionTapped({
            trail_id: trailDetails.id.toString(),
            trail_page_action: 'open map',
            trail_page_location: 'mobile cta bar'
          });
          if (!user) {
            setIsSignUpModalOpen(true);
          } else {
            openTrailMap();
          }
        }}
        text={<FormattedMessage defaultMessage="Open map" />}
      />
      {isDownloadModalOpen && (
        <HitTheTrailModal
          isOpen={isDownloadModalOpen}
          onCloseRequest={() => {
            setIsDownloadModalOpen(false);
          }}
          title={<FormattedMessage defaultMessage="Download" />}
          trailDetails={trailDetails}
          trailPage={trailPage}
        />
      )}
      {isSignUpModalOpen && (
        <SignUpModal
          closeModal={() => setIsSignUpModalOpen(false)}
          deeplinkData={{ pageId: trailDetails.id, pageType: 'trail' }}
          isOpen={isSignUpModalOpen}
          onSuccess={() => {
            setIsSignUpModalOpen(false);
            openTrailMap();
          }}
          trigger="Hit the Trail Card Open Map"
          upsellTrigger="Hit the Trail Card Open Map"
        />
      )}
      {isShareModalOpen && (
        <ShareModal
          isOpen
          onClose={() => setIsShareModalOpen(false)}
          onShareOptionClick={(shareOption: TrailDetailsShareOption) => {
            logTrailDetailsShareThisTrailOptionClicked({ share_option_clicked: shareOption, trail_id: trailDetails.id.toString() });
          }}
          shareObject={getTrailShareObject(trailDetails)}
        />
      )}
      {listModal}
      {signUpModal}
    </div>
  );
}
